import pdfMake from 'pdfmake/build/pdfmake';
import 'pdfmake/build/vfs_fonts';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import tinycolor from 'tinycolor2';
import { imageToBase64 } from 'Common.functions';
import { getFlexEventDisplayName, sortFlexEventsForDisplay } from '../flexEvents';
import { getTextWidth } from '../schedule';
import rezRateLogo from './rezRateLogo.png';

// Initialize Day.js plugins
dayjs.extend(utc);
dayjs.extend(timezone);
let rezRateLogoBase64 = null;

imageToBase64({
  url: rezRateLogo,
  callback: (err, base64Img) => {
    if (err) {
      console.error('Error converting rezRate logo to base64:', err);
    } else {
      rezRateLogoBase64 = base64Img;
    }
  },
});

const tableLayouts = {
  padlessTable: {
    hLineWidth: (i, node) => {
      if (i === 0 || i === node.table.body.length) {
        return 0;
      }
      return i === node.table.headerRows ? 1 : 1;
    },
    vLineWidth: (i) => {
      return 0;
    },
    hLineColor: (i) => {
      return i === 1 ? 'black' : '#aaa';
    },
    paddingLeft: (i) => {
      return 0;
    },
    paddingRight: (i, node) => {
      return 0;
    },
    paddingTop: (i, node) => {
      return 0;
    },
    paddingBottom: (i, node) => {
      return 0;
    },
  },
};

const FLEX_EVENT_PDF_LINE_HEIGHT = 1.15;
const PDF_PAGE_WIDTHS = {
  portrait: 419.53,
  landscape: 595.28,
};

const getVisibleFlexEvents = (flexEvents = []) => {
  return sortFlexEventsForDisplay(
    flexEvents.filter((flexEvent) => {
      const eventName = getFlexEventDisplayName(flexEvent);

      return eventName && eventName.toLowerCase() !== 'unavailable';
    }),
  );
};

const getRenderableScheduleBlocks = (scheduleBlockEntries = []) => {
  const blocksWithVisibleContent = scheduleBlockEntries.filter((scheduleBlockEntry) => {
    const hasCandidates = scheduleBlockEntry.Candidates && scheduleBlockEntry.Candidates.length > 0;
    const hasVisibleFlexEvents = getVisibleFlexEvents(scheduleBlockEntry.FlexEvents || []).length > 0;

    return hasCandidates || hasVisibleFlexEvents;
  });

  if (blocksWithVisibleContent.length > 0) {
    return blocksWithVisibleContent;
  }

  if (scheduleBlockEntries.length > 0) {
    return [scheduleBlockEntries[0]];
  }

  return [{}];
};

const estimateWrappedLineCount = ({ text = '', maxWidth, fontSize }) => {
  if (!text || !maxWidth || maxWidth <= 0) {
    return 1;
  }

  const words = text.split(' ');
  let lineCount = 1;
  let currentLine = '';

  words.forEach((word) => {
    const nextLine = currentLine ? `${currentLine} ${word}` : word;
    const nextLineWidth = getTextWidth({ text: nextLine, fontSize: `${fontSize}px` });

    if (nextLineWidth <= maxWidth) {
      currentLine = nextLine;
      return;
    }

    if (!currentLine) {
      const wordWidth = getTextWidth({ text: word, fontSize: `${fontSize}px` });
      lineCount += Math.max(1, Math.ceil(wordWidth / maxWidth)) - 1;
      currentLine = '';
      return;
    }

    lineCount += 1;
    currentLine = word;
  });

  return lineCount;
};

const estimateFlexBlockHeight = ({ flexEvents = [], maxWidth, fontSize }) => {
  const totalWrappedLines = flexEvents.reduce((lineTotal, flexEvent) => {
    const labelPrefix = flexEvents.length > 1 ? '• ' : '';

    return (
      lineTotal +
      estimateWrappedLineCount({
        text: `${labelPrefix}${getFlexEventDisplayName(flexEvent)}`,
        maxWidth,
        fontSize,
      })
    );
  }, 0);

  const lineCount = Math.max(totalWrappedLines, 1);

  return Math.ceil(lineCount * fontSize * FLEX_EVENT_PDF_LINE_HEIGHT + 4);
};

const createPageHeader = (data) => {
  const { interviewDateRow, base64Img, department } = data;
  const { scheduleName, interviewDate } = interviewDateRow;
  const { OrganizationName, DepartmentName, TimeZone } = department;
  const columns = [
    [
      {
        text: `${OrganizationName}`,
        alignment: 'left',
        fontSize: 5,
        bold: true,
        margin: [5, 2, 10, 0],
        width: 'auto',
        maxHeight: 15,
      },
      {
        text: `${DepartmentName}`,
        alignment: 'left',
        fontSize: 5,
        bold: false,
        margin: [5, 0, 10, 0],
        width: 'auto',
        maxHeight: 15,
      },
      {
        text: `${interviewDate ? dayjs(interviewDate).tz(TimeZone).format('dddd, MMMM DD, YYYY') : 'No Date'} ${
          scheduleName ? `(${scheduleName})` : ''
        }`,
        alignment: 'left',
        fontSize: 4,
        bold: false,
        margin: [5, 0, 10, 0],
        width: 'auto',
        maxHeight: 15,
      },
    ],
  ];

  if (base64Img && (base64Img[0] === '/' || base64Img[0] === 'i')) {
    // columns.splice(0, 0, {
    //   image: base64Img ? `data:image/png;base64,${base64Img}` : null,
    //   width: 20,
    //   height: 20,
    // });
  } else {
    columns.splice(0, 0, {
      text: ' ',
      width: 30,
    });
  }

  return {
    columns,
    margin: [10, 10, 10, 0],
  };
};

const createHeaders = (scheduleData, columnTracker, PDFFontSize) => {
  const { headers, metaData = {} } = scheduleData;
  const tableHeaders = [];
  const rawTableHeadersWithRooms = [];
  const rawTableHeadersWithoutRooms = [];

  const headerOrder = [];
  const hasFlexColumn = (metaData.FlexEvents || []).some((flexEvent) => {
    const eventName = getFlexEventDisplayName(flexEvent);

    return eventName && eventName.toLowerCase() !== 'unavailable';
  });

  headers.forEach((header) => {
    const { Evaluators = [], RoomLabel = '' } = header;
    let headerLabel = '';
    let headerNames = [];
    const key = header.pk_ScheduleAssignment.toString();
    const candidateCount = columnTracker[key] ? columnTracker[key].length : 0;
    if (header.Evaluators) {
      header.Evaluators.Evaluators.forEach((e, i) => {
        headerNames.push(`${e.UserFirst ? `${e.UserFirst[0]}.` : ''} ${e.UserLast}`);
      });
    }

    headerNames.sort();
    headerNames = headerNames.join(',');
    headerLabel += `${headerNames.replace(/,/g, ', ')}`;

    // headerOrder.push({
    //   pk_ScheduleAssignment: header.pk_ScheduleAssignment,
    //   Label: headerLabel,
    //   RoomLabel: RoomLabel || '',
    // });

    if (RoomLabel) {
      rawTableHeadersWithRooms.push([
        {
          text: headerLabel,
          style: { fontSize: PDFFontSize, bold: true, alignment: 'center' },
          pk_ScheduleAssignment: header.pk_ScheduleAssignment,
        },
        { text: `${RoomLabel || ' '}`, style: { fontSize: PDFFontSize - 1, alignment: 'center' } },
      ]);
    } else {
      rawTableHeadersWithoutRooms.push([
        {
          text: headerLabel,
          style: { fontSize: PDFFontSize, bold: true, alignment: 'center' },
          pk_ScheduleAssignment: header.pk_ScheduleAssignment,
        },
        { text: `${RoomLabel || ' '}`, style: { fontSize: PDFFontSize - 1, alignment: 'center' } },
      ]);
    }
  });

  rawTableHeadersWithRooms.sort((a, b) => {
    const textARoomLabel = a[1].text.toUpperCase();
    const textBRoomLabel = b[1].text.toUpperCase();

    return textARoomLabel < textBRoomLabel ? -1 : textARoomLabel > textBRoomLabel ? 1 : 0;
  });

  rawTableHeadersWithoutRooms.sort((a, b) => {
    const textA = a[0].text.toUpperCase();
    const textB = b[0].text.toUpperCase();

    return textA < textB ? -1 : textA > textB ? 1 : 0;
  });

  rawTableHeadersWithRooms.forEach((header) => {
    tableHeaders.push(header);
  });

  rawTableHeadersWithoutRooms.forEach((header) => {
    tableHeaders.push(header);
  });

  tableHeaders.forEach((h) => {
    const { pk_ScheduleAssignment } = h[0];

    headerOrder.push({
      pk_ScheduleAssignment: pk_ScheduleAssignment,
      Label: h[1].text || '',
      RoomLabel: h[0].text || '',
    });
  });

  tableHeaders.splice(0, 0, {
    text: 'Time',
    style: { fontWeight: 'bold', fontSize: PDFFontSize + 2, alignment: 'center' },
  });

  if (hasFlexColumn) {
    tableHeaders.push({
      text: 'Flex Events',
      style: { fontWeight: 'bold', fontSize: PDFFontSize + 2, alignment: 'center' },
    });
  }

  return { tableHeaders, headerOrder, hasFlexColumn };
};

const createRows = (scheduleData, columnTracker, candidates, PDFFontSize, PDFOrientation, TimeZone) => {
  const rows = [];
  const { tableHeaders, headerOrder, hasFlexColumn } = createHeaders(scheduleData, columnTracker, PDFFontSize);
  const totalContentColumns = tableHeaders.length - 1;
  const pageWidth = PDF_PAGE_WIDTHS[PDFOrientation] || PDF_PAGE_WIDTHS.portrait;
  const contentWidth = pageWidth - 20;
  const timeColumnWidth = PDFFontSize * 6.25;
  const contentColumnWidth = (contentWidth - timeColumnWidth) / totalContentColumns;
  const flexEventTextWidth = Math.max(contentColumnWidth - 6, 20);

  rows.push(tableHeaders);

  const cellLayout = {
    paddingLeft: (i) => {
      return 0;
    },
    paddingRight: (i, node) => {
      return 0;
    },
    paddingTop: (i, node) => {
      return 0;
    },
    paddingBottom: (i, node) => {
      return 0;
    },
  };

  scheduleData.body.forEach((row, i) => {
    const timeCell = {
      text: `${dayjs(row.slot)
        .tz(TimeZone)
        .format('hh:mm A')}`,
      border: [false, false, false, false],
      style: { alignment: 'center' },
    };
    if (row.CustomMeetingTitle || row.CustomDurationInMinutes || row.CustomPassingDurationInMinutes) {
      timeCell.style.fillColor = '#ffec45';
    }
    const newRow = [
      {
        layout: cellLayout,
        table: {
          widths: ['*'],
          body: [
            [
              {
                layout: cellLayout,
                table: {
                  widths: ['*'],
                  body: [[timeCell]],
                  margin: [0, 0, 0, 0],
                },
                border: [false, false, false, false],
              },
            ],
          ],
          margin: [0, 0, 0, 0],
        },
      },
    ];

    if (row.pk_Timeslot === 'END') {
      newRow.push({
        layout: cellLayout,
        table: {
          widths: ['*'],
          body: [
            [
              {
                layout: cellLayout,
                table: {
                  widths: ['*'],
                  body: [
                    [
                      {
                        text: row.endTimesText,
                        fillColor: '#d4d4d4',
                        border: [false, false, false, false],
                        margin: [0, 0, 0, 0],

                        style: { alignment: 'center', bold: true },
                      },
                    ],
                  ],
                },
                border: [false, false, false, false],
                margin: [0, 0, 0, 0],
              },
            ],
          ],
          margin: [0, 0, 0, 0],
        },
        colSpan: totalContentColumns,
      });
    } else {
      const singleLineBlockHeight = Math.ceil(PDFFontSize * FLEX_EVENT_PDF_LINE_HEIGHT + 4);
      const rowFlexEvents = hasFlexColumn ? getVisibleFlexEvents(row.FlexEvents || []) : [];
      const rowHasSplitCells = headerOrder.some((order) => {
        const scheduleBlockEntries = row[order.pk_ScheduleAssignment].ScheduleBlockEntries || [];

        return getRenderableScheduleBlocks(scheduleBlockEntries).length > 1;
      });
      const rowMaxCellHeight = headerOrder.reduce((currentMax, order) => {
        const scheduleBlockEntries = row[order.pk_ScheduleAssignment].ScheduleBlockEntries || [];
        const renderableScheduleBlocks = getRenderableScheduleBlocks(scheduleBlockEntries);

        if (renderableScheduleBlocks.length === 0) {
          return currentMax > singleLineBlockHeight ? currentMax : singleLineBlockHeight;
        }

        const maxFlexBlockHeight = renderableScheduleBlocks.reduce((cellMax, scheduleBlockEntry) => {
          const visibleFlexEvents = getVisibleFlexEvents(scheduleBlockEntry.FlexEvents || []);

          if (visibleFlexEvents.length === 0) {
            return cellMax;
          }

          const blockHeight = estimateFlexBlockHeight({
            flexEvents: visibleFlexEvents,
            maxWidth: flexEventTextWidth,
            fontSize: PDFFontSize,
          });

          return blockHeight > cellMax ? blockHeight : cellMax;
        }, 0);

        const normalizedBlockHeight = maxFlexBlockHeight > 0 ? maxFlexBlockHeight : singleLineBlockHeight;
        const cellHeight = normalizedBlockHeight * renderableScheduleBlocks.length;

        return cellHeight > currentMax ? cellHeight : currentMax;
      }, hasFlexColumn && rowFlexEvents.length > 0
        ? estimateFlexBlockHeight({
            flexEvents: rowFlexEvents,
            maxWidth: flexEventTextWidth,
            fontSize: PDFFontSize,
          })
        : singleLineBlockHeight);

      headerOrder.forEach((order) => {
        const newCell = {
          layout: cellLayout,
          table: {
            widths: ['*'],
            body: [],
            margin: [0, 0, 0, 0],
          },
        };
        const scheduleBlockEntries = row[order.pk_ScheduleAssignment].ScheduleBlockEntries || [];
        const normalizedScheduleBlockEntries = getRenderableScheduleBlocks(scheduleBlockEntries);
        const cellSplitCount = normalizedScheduleBlockEntries.length;
        const defaultBlockHeight =
          cellSplitCount === 1
            ? rowMaxCellHeight
            : Math.floor(rowMaxCellHeight / cellSplitCount) + (rowMaxCellHeight % cellSplitCount > 0 ? 1 : 0);
        const buildNormalizedBlockTable = (blockBody, explicitHeight = defaultBlockHeight) => {
          const blockTable = {
            widths: ['*'],
            body: [blockBody],
          };

          if (explicitHeight > 0) {
            blockTable.heights = [explicitHeight];
          }

          return blockTable;
        };
        const getCenteredTopMargin = (contentHeight, explicitHeight = defaultBlockHeight) => {
          if (explicitHeight <= 0) {
            return 0;
          }

          return Math.max(Math.ceil((explicitHeight - contentHeight) / 2), 0);
        };
        const singleLineContentHeight = PDFFontSize * FLEX_EVENT_PDF_LINE_HEIGHT;
        const splitBaseHeight =
          cellSplitCount > 1 ? Math.floor(rowMaxCellHeight / cellSplitCount) : rowMaxCellHeight;
        const splitRemainder = cellSplitCount > 1 ? rowMaxCellHeight % cellSplitCount : 0;
        const buildWrappedSingleBlock = ({
          blockBody,
          explicitHeight,
          fillColor = null,
          margin = [1, 0, 1, 0],
          topInset = 1,
          bottomInset = 0,
        }) => {
          const innerHeight = Math.max(explicitHeight - topInset - bottomInset, 1);

          return {
            layout: cellLayout,
            table: {
              widths: ['*'],
              body: [
                [
                  {
                    fillColor,
                    layout: cellLayout,
                    table: buildNormalizedBlockTable(blockBody, innerHeight),
                    border: [false, false, false, false],
                    margin: [0, topInset, 0, bottomInset],
                  },
                ],
              ],
              heights: [explicitHeight],
              margin: [0, 0, 0, 0],
            },
            border: [false, false, false, false],
            margin,
          };
        };

        normalizedScheduleBlockEntries.forEach((block, blockIndex) => {
          const explicitBlockHeight =
            cellSplitCount === 1 ? rowMaxCellHeight : splitBaseHeight + (blockIndex < splitRemainder ? 1 : 0);
          const shouldUseSingleBlockWrapper = rowHasSplitCells && cellSplitCount === 1;
          const wrapperTopInset = shouldUseSingleBlockWrapper ? 1 : 0;
          const wrapperBottomInset = 0;
          const effectiveContentHeight = shouldUseSingleBlockWrapper
            ? Math.max(explicitBlockHeight - wrapperTopInset - wrapperBottomInset, 1)
            : explicitBlockHeight;
          let newBlock = [];
          const blockFlexEvents = getVisibleFlexEvents(block.FlexEvents || []);

          if (row[order.pk_ScheduleAssignment].cellState === 'Unavailable') {
            newBlock.push({
              text: `UNAVAILABLE`,
              fillColor: shouldUseSingleBlockWrapper ? null : '#ff3d3d',
              color: '#ffffff',
              border: [false, false, false, false],
              margin: [0, getCenteredTopMargin(singleLineContentHeight, effectiveContentHeight), 0, 0],
              alignment: 'center',
            });
            newCell.table.body.push([
              shouldUseSingleBlockWrapper
                ? buildWrappedSingleBlock({
                    blockBody: newBlock,
                    explicitHeight: explicitBlockHeight,
                    fillColor: '#ff3d3d',
                    margin: [0, 0, 0, 0],
                    topInset: wrapperTopInset,
                    bottomInset: wrapperBottomInset,
                  })
                : {
                    layout: cellLayout,
                    table: buildNormalizedBlockTable(newBlock, explicitBlockHeight),
                    border: [false, false, false, false],
                    margin: [0, 0, 0, 0],
                  },
            ]);
          } else if (block.Candidates && block.Candidates.length > 0) {
            block.Candidates.forEach((candidate) => {
              newBlock = [];
              let fillColor = candidates[candidate.pk_Candidate] ? candidates[candidate.pk_Candidate].color : '#ffffff';
              if (row[order.pk_ScheduleAssignment].cellState === 'Warning') {
                fillColor = '#ffc180';
              }
              newBlock.push({
                text: `${candidate.LastName}, ${candidate.FirstName}  ${
                  row[order.pk_ScheduleAssignment].cellState === 'Warning' ? '(!)' : ''
                }`,
                fillColor: shouldUseSingleBlockWrapper ? null : fillColor,
                color: tinycolor(fillColor).isLight() ? '#000000' : '#ffffff',
                border: [false, false, false, false],
                margin: [0, getCenteredTopMargin(singleLineContentHeight, effectiveContentHeight), 0, 0],
                alignment: 'center',
              });

              newCell.table.body.push([
                shouldUseSingleBlockWrapper
                  ? buildWrappedSingleBlock({
                      blockBody: newBlock,
                      explicitHeight: explicitBlockHeight,
                      fillColor,
                      margin: [0, 0, 0, 0],
                      topInset: wrapperTopInset,
                      bottomInset: wrapperBottomInset,
                    })
                  : {
                      layout: cellLayout,
                      table: buildNormalizedBlockTable(newBlock, explicitBlockHeight),
                      border: [false, false, false, false],
                      margin: [0, 0, 0, 0],
                    },
              ]);
            });
          } else if (blockFlexEvents.length > 0) {
            const flexEventFillColor = '#d4d4d4';
            const flexBlockContentHeight = estimateFlexBlockHeight({
              flexEvents: blockFlexEvents,
              maxWidth: flexEventTextWidth,
              fontSize: PDFFontSize,
            });

            newBlock.push({
              text: blockFlexEvents
                .map((flexEvent) => {
                  const eventName = getFlexEventDisplayName(flexEvent);

                  return blockFlexEvents.length > 1 ? `• ${eventName}` : eventName;
                })
                .join('\n'),
              fillColor: shouldUseSingleBlockWrapper ? null : flexEventFillColor,
              color: tinycolor(flexEventFillColor).isLight() ? '#000000' : '#ffffff',
              border: [false, false, false, false],
              margin: [0, getCenteredTopMargin(flexBlockContentHeight, effectiveContentHeight), 0, 0],
              alignment: 'left',
              lineHeight: FLEX_EVENT_PDF_LINE_HEIGHT,
            });

            newCell.table.body.push([
              shouldUseSingleBlockWrapper
                ? buildWrappedSingleBlock({
                    blockBody: newBlock,
                    explicitHeight: explicitBlockHeight,
                    fillColor: flexEventFillColor,
                    topInset: wrapperTopInset,
                    bottomInset: wrapperBottomInset,
                  })
                : {
                    layout: cellLayout,
                    table: buildNormalizedBlockTable(newBlock, explicitBlockHeight),
                    border: [false, false, false, false],
                    margin: [1, 0, 1, 0],
                  },
            ]);
          } else if (row.CustomMeetingTitle) {
            newBlock.push({
              text: row.CustomMeetingTitle,
              fillColor: shouldUseSingleBlockWrapper ? null : '#d4d4d4',
              border: [false, false, false, false],
              margin: [0, getCenteredTopMargin(singleLineContentHeight, effectiveContentHeight), 0, 0],
              alignment: 'center',
            });
            newCell.table.body.push([
              shouldUseSingleBlockWrapper
                ? buildWrappedSingleBlock({
                    blockBody: newBlock,
                    explicitHeight: explicitBlockHeight,
                    fillColor: '#d4d4d4',
                    topInset: wrapperTopInset,
                    bottomInset: wrapperBottomInset,
                  })
                : {
                    layout: cellLayout,
                    table: buildNormalizedBlockTable(newBlock, explicitBlockHeight),
                    border: [false, false, false, false],
                    margin: [1, 0, 1, 0],
                  },
            ]);
          } else {
            // EMPTY CELL
            newBlock = [
              {
                text: '\u00A0',
                fillColor: shouldUseSingleBlockWrapper ? null : '#d4d4d4',
                border: [false, false, false, false],
                margin: [0, getCenteredTopMargin(singleLineContentHeight, effectiveContentHeight), 0, 0],
                lineHeight: FLEX_EVENT_PDF_LINE_HEIGHT,
                style: { color: '#d4d4d4' },
              },
            ];
            newCell.table.body.push([
              shouldUseSingleBlockWrapper
                ? buildWrappedSingleBlock({
                    blockBody: newBlock,
                    explicitHeight: explicitBlockHeight,
                    fillColor: '#d4d4d4',
                    topInset: wrapperTopInset,
                    bottomInset: wrapperBottomInset,
                  })
                : {
                    layout: cellLayout,
                    table: buildNormalizedBlockTable(newBlock, explicitBlockHeight),
                    border: [false, false, false, false],
                    margin: [1, 0, 1, 0],
                  },
            ]);
          }
        });

        newRow.push(newCell);
      });

      if (hasFlexColumn) {
        const flexColumnContentHeight =
          rowFlexEvents.length > 0
            ? estimateFlexBlockHeight({
                flexEvents: rowFlexEvents,
                maxWidth: flexEventTextWidth,
                fontSize: PDFFontSize,
              })
            : singleLineBlockHeight;
        const flexColumnShouldUseWrapper = rowHasSplitCells;
        const flexColumnTopInset = flexColumnShouldUseWrapper ? 1 : 0;
        const flexColumnBottomInset = 0;
        const flexColumnEffectiveContentHeight = flexColumnShouldUseWrapper
          ? Math.max(rowMaxCellHeight - flexColumnTopInset - flexColumnBottomInset, 1)
          : rowMaxCellHeight;
        const flexColumnTopMargin = Math.max(
          Math.ceil((flexColumnEffectiveContentHeight - flexColumnContentHeight) / 2),
          0,
        );
        const flexCellFillColor = rowFlexEvents.length > 1 ? '#ff9703' : '#d4d4d4';
        const flexColumnTextCell = {
          text:
            rowFlexEvents.length > 0
              ? rowFlexEvents
                  .map((flexEvent) => {
                    return getFlexEventDisplayName(flexEvent);
                  })
                  .join('\n')
              : ' ',
          fillColor: flexColumnShouldUseWrapper ? null : rowFlexEvents.length > 0 ? flexCellFillColor : '#d4d4d4',
          color: rowFlexEvents.length > 0 && !tinycolor(flexCellFillColor).isLight() ? '#ffffff' : '#000000',
          border: [false, false, false, false],
          margin: [0, flexColumnTopMargin, 0, 0],
          alignment: 'left',
          lineHeight: FLEX_EVENT_PDF_LINE_HEIGHT,
        };

        newRow.push({
          layout: cellLayout,
          table: {
            widths: ['*'],
            body: [
              [
                flexColumnShouldUseWrapper
                  ? {
                      layout: cellLayout,
                      table: {
                        widths: ['*'],
                        body: [
                          [
                            {
                              fillColor: rowFlexEvents.length > 0 ? flexCellFillColor : '#d4d4d4',
                              layout: cellLayout,
                              table: {
                                widths: ['*'],
                                body: [[flexColumnTextCell]],
                                heights: [Math.max(rowMaxCellHeight - flexColumnTopInset - flexColumnBottomInset, 1)],
                                margin: [0, 0, 0, 0],
                              },
                              border: [false, false, false, false],
                              margin: [0, flexColumnTopInset, 0, flexColumnBottomInset],
                            },
                          ],
                        ],
                        heights: [rowMaxCellHeight],
                        margin: [0, 0, 0, 0],
                      },
                      border: [false, false, false, false],
                      margin: [0, 0, 0, 0],
                    }
                  : {
                      layout: cellLayout,
                      table: {
                        widths: ['*'],
                        body: [[flexColumnTextCell]],
                        heights: [rowMaxCellHeight],
                        margin: [0, 0, 0, 0],
                      },
                      border: [false, false, false, false],
                      margin: [0, 0, 0, 0],
                    },
              ],
            ],
            margin: [0, 0, 0, 0],
          },
        });
      }
    }

    rows.push(newRow);
  });
  return rows;
};

const createPDFProper = (data) => {
  const {
    interviewDateRow,
    scheduleData,
    columnTracker,
    candidates,
    department,
    base64Img,
    PDFOrientation,
    PDFFontSize,
    willGetDataUrl,
    callback,
  } = data;

  const tableWidths = scheduleData.headers.map((h) => {
    return '*';
  });
  tableWidths.splice(0, 0, PDFFontSize * 6.25);
  const hasFlexColumn = (scheduleData.metaData && scheduleData.metaData.FlexEvents
    ? scheduleData.metaData.FlexEvents
    : []
  ).some((flexEvent) => {
    const eventName = getFlexEventDisplayName(flexEvent);

    return eventName && eventName.toLowerCase() !== 'unavailable';
  });

  if (hasFlexColumn) {
    tableWidths.push('*');
  }

  let pdfObject = [
    {
      layout: 'padlessTable', //'lightHorizontalLines', // optional
      table: {
        headerRows: 1,
        widths: tableWidths,
        body: [...createRows(scheduleData, columnTracker, candidates, PDFFontSize, PDFOrientation, department.TimeZone)],
      },
    },
  ];

  const docDefinition = {
    // header: createPageHeader({ interviewDateRow, base64Img, department }),
    pageSize: 'A5',
    pageOrientation: PDFOrientation,
    content: pdfObject,
    defaultStyle: {
      fontSize: PDFFontSize || 4,
    },
    pageMargins: [10, 35, 10, 5],
  };

  const pdfDocGenerator = pdfMake.createPdf(docDefinition, tableLayouts);

  if (willGetDataUrl) {
    pdfDocGenerator.getDataUrl((dataURL) => {
      if (callback) {
        callback(dataURL);
      }
    });
  } else {
    // Opera 8.0+
    const isOpera =
      (!!window.opr && !!window.opr.addons) || !!window.opera || navigator.userAgent.indexOf(' OPR/') >= 0;

    // Firefox 1.0+
    const isFirefox = typeof InstallTrigger !== 'undefined';

    // Safari 3.0+ "[object HTMLElementConstructor]"
    const isSafari =
      /constructor/i.test(window.HTMLElement) ||
      (function(p) {
        return p.toString() === '[object SafariRemoteNotification]';
      })(!window['safari'] || (typeof safari !== 'undefined' && window['safari'].pushNotification));

    // Internet Explorer 6-11
    const isIE = /*@cc_on!@*/ false || !!document.documentMode;

    // Edge 20+
    const isEdge = !isIE && !!window.StyleMedia;

    // Chrome 1 - 79
    const isChrome = !!window.chrome && (!!window.chrome.webstore || !!window.chrome.runtime);

    // Edge (based on chromium) detection
    const isEdgeChromium = isChrome && navigator.userAgent.indexOf('Edg') != -1;

    // Blink engine detection
    const isBlink = (isChrome || isOpera) && !!window.CSS;

    const { scheduleName, interviewDate } = interviewDateRow;
    const downloadTitle = `${department.DepartmentName} - ${
      interviewDate ? dayjs(interviewDate).tz(department.TimeZone).format('MMM DD, YYYY') : 'No Date'
    } ${scheduleName ? `(${scheduleName})` : ''}`;
    pdfDocGenerator.download(downloadTitle);
  }
};

export const createPDFForSchedule = ({
  interviewDateRow,
  scheduleData,
  columnTracker,
  candidates = [],
  department,
  PDFOrientation,
  PDFFontSize,
  willGetDataUrl,
  callback,
}) => {
  if (department && department.DepartmentLogo) {
    imageToBase64({
      url: department.DepartmentLogo,
      callback: (err, base64Img) => {
        createPDFProper({
          interviewDateRow,
          scheduleData,
          columnTracker,
          candidates,
          department,
          base64Img: err ? rezRateLogoBase64 : base64Img,
          PDFOrientation,
          PDFFontSize,
          willGetDataUrl,
          callback,
        });
      },
    });
  } else {
    createPDFProper({
      interviewDateRow,
      scheduleData,
      columnTracker,
      candidates,
      department,
      PDFOrientation,
      PDFFontSize,
      willGetDataUrl,
      callback,
    });
  }
};
